import React, { useState } from 'react';
import axios from 'axios';
import { Form, Button, Alert } from 'react-bootstrap';
import VideoPopup from './VideoPopup';
import DocumentPopup from './DocumentPopup';
import DragDropFileUpload from './DragDropFileUpload';
import './UploadWebData.css';

const UploadWebData = ({ webData, refetchData }) => {
    const [video, setVideo] = useState(null);
    const [document, setDocument] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showVideoPopup, setShowVideoPopup] = useState(false);
    const [showDocumentPopup, setShowDocumentPopup] = useState(false);
    const [deleteModal, setDeleteModal] = useState({ show: false, type: '', id: '', name: '' });

    // File handlers
    const handleVideoSelect = (files) => {
        // Handle both single file and file array - take the first file
        const fileArray = Array.isArray(files) ? files : [files];
        setVideo(fileArray[0] || null);
    };

    const handleVideoRemove = () => {
        setVideo(null);
    };

    const handleDocumentSelect = (files) => {
        // Handle both single file and file array - take the first file
        const fileArray = Array.isArray(files) ? files : [files];
        setDocument(fileArray[0] || null);
    };

    const handleDocumentRemove = () => {
        setDocument(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        const formData = new FormData();
        if (video) formData.append('video', video);
        if (document) formData.append('document', document);

        try {
            const _response = await axios.post('/api/upload-webdata', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                withCredentials: true
            });
            setSuccess('Web data uploaded successfully!');
            setVideo(null);
            setDocument(null);
            if (refetchData && typeof refetchData === 'function') {
                refetchData(); 
            }
        } catch (err) {
            console.error('Upload error:', err);
            setError(err.response?.data?.message || err.response?.data?.error || 'An error occurred while uploading web data.');
        }
    };
    
    const handleDelete = (type, id, name) => {
        setDeleteModal({ show: true, type, id, name });
    };

    const confirmDelete = async () => {
        try {
            if (deleteModal.type === 'video') {
                const _response = await axios.post(`/api/delete-video/${deleteModal.id}`, {}, { withCredentials: true });
            } else {
                const _response = await axios.post(`/api/delete-document/${deleteModal.id}`, {}, { withCredentials: true });
            }
            if (refetchData && typeof refetchData === 'function') {
                refetchData(); 
            }
            setDeleteModal({ show: false, type: '', id: '', name: '' });
        } catch (err) {
            console.error(`Failed to delete ${deleteModal.type}`, err);
            console.error('Error details:', err.response?.data);
        }
    };

    const cancelDelete = () => {
        setDeleteModal({ show: false, type: '', id: '', name: '' });
    };

    return (
        <div className="upload-webdata-container">
            <h1>Upload WebData</h1>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}
            
            <Form onSubmit={handleSubmit} className="form">
                <Form.Group className="mb-3" controlId="video-file-webdata">
                    <Form.Label>Video:</Form.Label>
                    <DragDropFileUpload
                        onFileSelected={handleVideoSelect}
                        onFileRemoved={handleVideoRemove}
                        selectedFile={video}
                        accept="video/*"
                        maxFileSize={100 * 1024 * 1024} // 100MB
                        supportedTypes={['.mp4', '.avi', '.mov', '.wmv']}
                        placeholder="Click to upload video or drag and drop"
                        description="Supports: MP4, AVI, MOV, WMV"
                    />
                </Form.Group>
                <Form.Group className="mb-3" controlId="document-file-webdata">
                    <Form.Label>Document:</Form.Label>
                    <DragDropFileUpload
                        onFileSelected={handleDocumentSelect}
                        onFileRemoved={handleDocumentRemove}
                        selectedFile={document}
                        accept=".pdf,.doc,.docx"
                        maxFileSize={10 * 1024 * 1024} // 10MB
                        supportedTypes={['.pdf', '.doc', '.docx']}
                        placeholder="Click to upload document or drag and drop"
                        description="Supports: PDF, DOC, DOCX"
                    />
                </Form.Group>
                <Button variant="primary" type="submit">Upload</Button>
            </Form>
            
            <div className="view-buttons">
                <h3>View WebData</h3>
                <Button variant="secondary" onClick={() => setShowVideoPopup(true)}>View Videos</Button>
                <Button variant="secondary" onClick={() => setShowDocumentPopup(true)}>View Documents</Button>
            </div>
            
            <VideoPopup 
                show={showVideoPopup} 
                handleClose={() => setShowVideoPopup(false)} 
                videos={(webData || []).filter(d => d.video).map(d => ({...d.video, dataId: d._id}))}
                handleDelete={(id, name) => handleDelete('video', id, name)}
            />
            <DocumentPopup 
                show={showDocumentPopup} 
                handleClose={() => setShowDocumentPopup(false)} 
                documents={(webData || []).filter(d => d.document).map(d => ({...d.document, dataId: d._id}))}
                handleDelete={(id, name) => handleDelete('document', id, name)}
            />

            {/* Delete Confirmation Modal */}
            {deleteModal.show && (
                <div className="modal-overlay">
                    <div className="delete-confirmation-modal">
                        <div className="modal-header">
                            <h3>Confirm Delete</h3>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete this {deleteModal.type}?</p>
                            <p className="file-name">"{deleteModal.name}"</p>
                            <p className="warning-text">This action cannot be undone.</p>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn btn-secondary"
                                onClick={cancelDelete}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-danger"
                                onClick={confirmDelete}
                            >
                                Delete {deleteModal.type}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UploadWebData; 